// UTILS
import {
	decodeJson,
	encodeJson
} from 'commons/js/utils/parser';

// CONFIG
import {ONE_DAY_IN_MILISECONDS} from 'commons/js/config/time';

/**
 * It checks if the current browser does support the
 * LocalStorage API or not.
 *
 * @returns {boolean}
 */
function checkStorage() {
	const hasLocalStorage = typeof localStorage !== 'undefined' && localStorage !== null;

	if (!hasLocalStorage) {
		console.warn('The LocalStorage API is not supported by the current browser.');
	}

	return hasLocalStorage;
}

/**
 * Class that should be used when it's needed to handle data
 * in localstorage.
 */
export default class LocalStorage {
	/**
	 * It exposes some operations like adding, removing,
	 * getting or clearing items/data in the browser storage.
	 */
	constructor() {
		this.hasLocalStorage = checkStorage();
	}

	/**
	 * It adds some data to the localstorage.
	 *
	 * @param {string} key - The key of the localstorage item.
	 * @param {string} value - The value to be attached to it.
	 */
	add(key, value) {
		if (!this.hasLocalStorage) {
			return;
		}

		try {
			const stringified = typeof value === 'string' ? value : encodeJson(value);

			localStorage.setItem(key, stringified);
		} catch (e) {
			const {code} = e;

			/**
			 * Error codes used by major browsers.
			 *
			 * @see {@link https://chrisberkhout.com/blog/localstorage-errors/} for further information.
			 */
			if (code === 22 || code === 1014) {
				console.error(`Quota exceeded for "${key}".`);

				return;
			}

			console.error('Unexpected error during a localstorage operation.', e);
		}
	}

	/**
	 * It gets some data from the localstorage.
	 *
	 * @param {string} key - The key of the localstorage item.
	 * @param {boolean} parsed - Defines wether the item must be parsed or not.
	 *
	 * @returns {object|array|string|number|boolean|null}
	 */
	get(key, parsed = false) {
		if (!this.hasLocalStorage) {
			return;
		}

		const item = localStorage.getItem(key);

		const result = parsed ? decodeJson(item) : item;

		return result;
	}

	/**
	 * It removes some data from the localstorage.
	 *
	 * @param {string} key - The key of the localstorage item.
   *
   * @returns {undefined}
	 */
	remove(key) {
		const item = this.get(key);

		if (!item) {
			return;
		}

		localStorage.removeItem(key);
	}

	/**
	 * It clears all localstorage items.
	 *
	 * @returns {void}
	 */
	reset() {
		if (!this.hasLocalStorage) {
			return;
		}

		localStorage.clear();
	}

	/**
	 * Check if if the record is existing
	 * and it didn't expired.
	 * @param {object} record The record instance
	 * @param {number} [expiration] The time (in ms) in which the record expires
	 * @returns {boolean} Indication if it's a valid record
	 */
	isValidRecord(record, expiration = ONE_DAY_IN_MILISECONDS) {
		const now = Date.now();

		if (!record || !Object.prototype.hasOwnProperty.call(record, 'timestamp')) {
			return false;
		}

		return now <= (record.timestamp + expiration);
	}
}
